import React, { useState, useEffect, useRef, useContext } from "react";
import Axios from "axios";
import { ReactReduxContext } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
    Form,
    Button,
    Drawer,
    Input,
    Spin,
    Skeleton,
    notification,
    Tag,
} from "antd";
import {
    ArrowLeftOutlined,
    CloseOutlined,
    DeleteOutlined,
    PlusOutlined,
    MessageOutlined,
    MenuOutlined,
    SendOutlined,
} from "@ant-design/icons";
import { nanoid } from "nanoid";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Tokens } from "Shared/Tokens";
import { AutoSyncBadge, BottomNavBar } from "Components/Layout/AppShell";
import LoginWall from "Components/Shared/LoginWall";

dayjs.extend(relativeTime);

const cleanChatTitle = (rawTitle) => {
    if (!rawTitle || typeof rawTitle !== "string") return "Untitled chat";
    const stripped = rawTitle.replace(/^[\s✨💡📊📈🔥🚀💬🤖]+/u, "").trim();
    return stripped || rawTitle.trim() || "Untitled chat";
};

const renderInline = (text) => {
    if (!text) return "";
    const parts = [];
    const regex = /(\*\*.*?\*\*|`.*?`|\*.*?\*)/g;
    const tokens = text.split(regex);

    tokens.forEach((token, idx) => {
        if (!token) return;
        const keyId = `inline-${idx}-${token.slice(0, 8)}`;
        if (token.startsWith("**") && token.endsWith("**")) {
            parts.push(
                <strong
                    key={keyId}
                    style={{ color: Tokens.textPrimary, fontWeight: 500 }}
                >
                    {token.slice(2, -2)}
                </strong>
            );
        } else if (token.startsWith("`") && token.endsWith("`")) {
            parts.push(
                <code
                    key={keyId}
                    style={{
                        backgroundColor: Tokens.raised,
                        padding: "2px 6px",
                        borderRadius: "4px",
                        fontSize: "13px",
                        fontFamily: Tokens.fontMono,
                        color: Tokens.textPrimary,
                    }}
                >
                    {token.slice(1, -1)}
                </code>
            );
        } else if (token.startsWith("*") && token.endsWith("*")) {
            parts.push(
                <span key={keyId} style={{ color: Tokens.textSecondary }}>
                    {token.slice(1, -1)}
                </span>
            );
        } else {
            parts.push(token);
        }
    });

    return parts;
};

const splitTableCells = (rowLine) => {
    const trimmed = rowLine.trim();
    const inner = trimmed.replace(/^\|/, "").replace(/\|$/, "");
    return inner.split("|").map((cell) => cell.trim());
};

const isTableSeparatorLine = (line) => {
    if (!line || !line.includes("|")) return false;
    const cells = splitTableCells(line);
    if (cells.length === 0) return false;
    return cells.every((c) => /^:?-{2,}:?$/.test(c));
};

const parseTableAlignments = (separatorLine, colCount) => {
    const cells = splitTableCells(separatorLine);
    return Array.from({ length: colCount }, (_, idx) => {
        const raw = cells[idx] || "";
        if (raw.startsWith(":") && raw.endsWith(":")) return "center";
        if (raw.endsWith(":")) return "right";
        return "left";
    });
};

const renderMarkdownTable = (tableKey, headerCells, alignments, bodyRows) => {
    const headerItems = headerCells.map((hCell, colIdx) => ({
        colKey: `${tableKey}-col-${hCell || colIdx}`,
        text: hCell,
        align: alignments[colIdx] || "left",
        isFirst: colIdx === 0,
    }));
    const rowItems = bodyRows.map((rowCells, rowIdx) => ({
        rowKey: `${tableKey}-row-${rowCells.join("|").slice(0, 24)}-${
            rowCells.length
        }-${rowIdx === bodyRows.length - 1 ? "last" : rowIdx}`,
        isLastRow: rowIdx === bodyRows.length - 1,
        cells: headerItems.map((col, colIdx) => ({
            cellKey: `${col.colKey}-${(rowCells[colIdx] || "").slice(0, 12)}`,
            text: rowCells[colIdx] || "",
            align: col.align,
            isFirst: col.isFirst,
        })),
    }));

    return (
        <div
            key={tableKey}
            data-testid="markdown-table-wrapper"
            style={{
                width: "100%",
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
                margin: "10px 0",
                borderRadius: Tokens.radiusControl,
                border: `1px solid ${Tokens.hairline}`,
                backgroundColor: Tokens.surface,
            }}
        >
            <table
                data-testid="markdown-table"
                style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "12.5px",
                    lineHeight: "1.5",
                }}
            >
                <thead>
                    <tr
                        style={{
                            backgroundColor: Tokens.raised,
                            borderBottom: `1px solid ${Tokens.hairline}`,
                        }}
                    >
                        {headerItems.map((col) => (
                            <th
                                key={col.colKey}
                                style={{
                                    padding: "8px 10px",
                                    textAlign: col.align,
                                    color: Tokens.textSecondary,
                                    fontWeight: 600,
                                    fontSize: "11.5px",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {renderInline(col.text)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rowItems.map((row) => (
                        <tr
                            key={row.rowKey}
                            style={{
                                borderBottom: row.isLastRow
                                    ? "none"
                                    : `1px solid ${Tokens.hairline}`,
                            }}
                        >
                            {row.cells.map((cell) => (
                                <td
                                    key={cell.cellKey}
                                    style={{
                                        padding: "8px 10px",
                                        textAlign: cell.align,
                                        color: Tokens.textPrimary,
                                        verticalAlign: "top",
                                        minWidth: cell.isFirst
                                            ? "56px"
                                            : "110px",
                                    }}
                                >
                                    {renderInline(cell.text)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export const MarkdownText = ({ content }) => {
    if (!content) return null;
    const lines = content.split("\n");
    const elements = [];
    let i = 0;

    while (i < lines.length) {
        const line = lines[i];
        const trimmed = line.trim();
        const lineKey = `md-line-${i}-${trimmed.slice(0, 10)}`;

        if (!trimmed) {
            elements.push(<div key={lineKey} style={{ height: "8px" }} />);
            i += 1;
        } else if (
            trimmed.includes("|") &&
            i + 1 < lines.length &&
            isTableSeparatorLine(lines[i + 1].trim())
        ) {
            const headerCells = splitTableCells(trimmed);
            const alignments = parseTableAlignments(
                lines[i + 1].trim(),
                headerCells.length
            );
            const bodyRows = [];
            let j = i + 2;
            while (j < lines.length) {
                const candidate = lines[j].trim();
                if (!candidate || !candidate.includes("|")) break;
                if (!isTableSeparatorLine(candidate)) {
                    bodyRows.push(splitTableCells(candidate));
                }
                j += 1;
            }

            elements.push(
                renderMarkdownTable(
                    `md-table-wrap-${i}`,
                    headerCells,
                    alignments,
                    bodyRows
                )
            );
            i = j;
        } else if (/^(-{3,}|\*{3,})$/.test(trimmed)) {
            elements.push(
                <hr
                    key={lineKey}
                    style={{
                        border: "none",
                        borderTop: `1px solid ${Tokens.hairline}`,
                        margin: "12px 0",
                    }}
                />
            );
            i += 1;
        } else if (trimmed.startsWith("#### ")) {
            elements.push(
                <h5
                    key={lineKey}
                    style={{
                        color: Tokens.textPrimary,
                        margin: "10px 0 6px",
                        fontSize: "14px",
                        fontWeight: 600,
                    }}
                >
                    {renderInline(trimmed.slice(5))}
                </h5>
            );
            i += 1;
        } else if (trimmed.startsWith("### ")) {
            elements.push(
                <h4
                    key={lineKey}
                    style={{
                        color: Tokens.textPrimary,
                        margin: "12px 0 8px",
                        fontSize: "15px",
                        fontWeight: 500,
                    }}
                >
                    {renderInline(trimmed.slice(4))}
                </h4>
            );
            i += 1;
        } else if (trimmed.startsWith("## ")) {
            elements.push(
                <h3
                    key={lineKey}
                    style={{
                        color: Tokens.textPrimary,
                        margin: "16px 0 8px",
                        fontSize: "17px",
                        fontWeight: 500,
                    }}
                >
                    {renderInline(trimmed.slice(3))}
                </h3>
            );
            i += 1;
        } else if (trimmed.startsWith("# ")) {
            elements.push(
                <h2
                    key={lineKey}
                    style={{
                        color: Tokens.textPrimary,
                        margin: "16px 0 8px",
                        fontSize: "18px",
                        fontWeight: 600,
                    }}
                >
                    {renderInline(trimmed.slice(2))}
                </h2>
            );
            i += 1;
        } else if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
            elements.push(
                <div
                    key={lineKey}
                    style={{
                        display: "flex",
                        gap: "8px",
                        margin: "4px 0",
                        paddingLeft: "4px",
                    }}
                >
                    <span style={{ color: Tokens.textSecondary }}>•</span>
                    <span>{renderInline(trimmed.slice(2))}</span>
                </div>
            );
            i += 1;
        } else {
            const matchNumber = trimmed.match(/^(\d+)\.\s+(.*)/);
            if (matchNumber) {
                elements.push(
                    <div
                        key={lineKey}
                        style={{
                            display: "flex",
                            gap: "8px",
                            margin: "4px 0",
                            paddingLeft: "4px",
                        }}
                    >
                        <span
                            style={{
                                color: Tokens.textSecondary,
                                fontWeight: 500,
                                fontVariantNumeric: "tabular-nums",
                                flexShrink: 0,
                                whiteSpace: "nowrap",
                            }}
                        >
                            {matchNumber[1]}.
                        </span>
                        <span>{renderInline(matchNumber[2])}</span>
                    </div>
                );
            } else {
                elements.push(
                    <p
                        key={lineKey}
                        style={{ margin: "4px 0", lineHeight: "1.6" }}
                    >
                        {renderInline(line)}
                    </p>
                );
            }
            i += 1;
        }
    }

    return <div>{elements}</div>;
};

const News = () => {
    document.title = `Ask AI — Portfolio`;
    const history = useHistory();
    const location = useLocation();
    const { chatId: routeChatIdParam, id: legacyRouteId } = useParams();
    const routeChatId = routeChatIdParam || legacyRouteId || null;
    const reduxContext = useContext(ReactReduxContext);
    const manualAssets =
        reduxContext?.store?.getState?.()?.assets?.assets || [];

    const [form] = Form.useForm();
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [disabled, setDisabled] = useState(true);

    const [conversations, setConversations] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(true);
    const [activeChatId, setActiveChatId] = useState(routeChatId || null);

    const [messages, setMessages] = useState([]);
    const [messagesLoading, setMessagesLoading] = useState(false);

    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const [chatSearch, setChatSearch] = useState("");
    const [requiresAuth, setRequiresAuth] = useState(false);

    const messagesEndRef = useRef(null);
    const justCreatedChatRef = useRef(null);
    const requestSeqRef = useRef(0);

    const isMobile = windowWidth < 768;

    useEffect(() => {
        const coinParam = new URLSearchParams(location?.search || "").get(
            "coin"
        );
        if (coinParam && !routeChatId) {
            const prompt = `Analyze my ${coinParam} position and latest market sentiment`;
            setQuery(prompt);
            setDisabled(false);
        }
    }, [location?.search, routeChatId]);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const scrollToBottom = () => {
        if (
            messagesEndRef.current &&
            typeof messagesEndRef.current.scrollIntoView === "function"
        ) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, loading]);

    const fetchConversationsList = async () => {
        setHistoryLoading(true);
        try {
            const api = `${process.env.REACT_APP_API}/binance/chats`;
            const res = await Axios.get(api, { withCredentials: true });
            const list =
                res.data?.data?.conversations || res.data?.conversations;
            setRequiresAuth(false);
            if (Array.isArray(list)) {
                setConversations(list);
            }
        } catch (error) {
            if (error.response?.status === 401) {
                setRequiresAuth(true);
            }
        } finally {
            setHistoryLoading(false);
        }
    };

    useEffect(() => {
        fetchConversationsList();
    }, []);

    const fetchMessages = async (chatId) => {
        if (!chatId) return;
        setMessagesLoading(true);
        try {
            const api = `${process.env.REACT_APP_API}/binance/chats/${chatId}/messages`;
            const res = await Axios.get(api, { withCredentials: true });
            const msgs = res.data?.data?.messages || res.data?.messages;
            if (Array.isArray(msgs)) {
                setMessages(msgs);
            }
        } catch (error) {
            if (error.response?.status === 401) {
                setRequiresAuth(true);
            } else {
                notification.error({
                    message: "Error",
                    description: "Failed to load chat history.",
                });
            }
        } finally {
            setMessagesLoading(false);
        }
    };

    useEffect(() => {
        if (routeChatId) {
            setActiveChatId(routeChatId);
            if (justCreatedChatRef.current === routeChatId) {
                justCreatedChatRef.current = null;
                return;
            }
            fetchMessages(routeChatId);
        } else {
            setActiveChatId(null);
            setMessages([]);
        }
    }, [routeChatId]);

    const handleSelectConversation = (chatId) => {
        if (chatId === activeChatId) {
            if (isMobile) setMobileMenuOpen(false);
            return;
        }
        requestSeqRef.current += 1;
        setLoading(false);
        history.push(`/imported/binance/ai-news/${chatId}`);
        if (isMobile) setMobileMenuOpen(false);
    };

    const handleNewChat = () => {
        requestSeqRef.current += 1;
        justCreatedChatRef.current = null;
        setLoading(false);
        setActiveChatId(null);
        setMessages([]);
        history.push("/imported/binance/ai-news");
        if (isMobile) setMobileMenuOpen(false);
    };

    const handleDeleteConversation = (e, chatId) => {
        e.stopPropagation();
        setConfirmDeleteId(chatId);
    };

    const confirmDeleteConversation = async (e, chatId) => {
        e.stopPropagation();
        setConfirmDeleteId(null);
        try {
            const api = `${process.env.REACT_APP_API}/binance/chats/${chatId}`;
            await Axios.delete(api, { withCredentials: true });
            notification.success({
                message: "Deleted",
                description: "Chat history deleted.",
            });
            setConversations((prev) => prev.filter((c) => c.id !== chatId));
            if (activeChatId === chatId) {
                handleNewChat();
            }
        } catch (err) {
            notification.error({
                message: "Error",
                description: "Failed to delete chat.",
            });
        }
    };

    const handleValueChange = (e) => {
        const val = e.target.value;
        setQuery(val);
        setDisabled(!val.trim());
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (!disabled && !loading) {
                form.submit();
            }
        }
    };

    const onFinish = async () => {
        const userPrompt = query.trim();
        if (!userPrompt || loading) return;

        requestSeqRef.current += 1;
        const currentSeq = requestSeqRef.current;
        const startingChatId = activeChatId;

        setQuery("");
        setDisabled(true);
        form.resetFields();

        const userMsgId = nanoid();
        const userMsg = {
            id: userMsgId,
            role: "user",
            content: userPrompt,
            createdAt: Date.now(),
        };

        setMessages((prev) => [...prev, userMsg]);
        setLoading(true);

        try {
            const api = `${process.env.REACT_APP_API}/binance/news`;
            const params = { q: userPrompt };
            if (startingChatId) {
                params.chatId = startingChatId;
            }
            try {
                const cachedSummary = JSON.parse(
                    localStorage.getItem("binance_wallet_summary_cache") || "{}"
                );
                if (cachedSummary?.cutoffDate) {
                    params.cutoffDate = cachedSummary.cutoffDate;
                }
            } catch (storageErr) {
                // ignore storage parse error
            }

            const res = await Axios.get(api, {
                params,
                withCredentials: true,
            });

            if (currentSeq !== requestSeqRef.current) {
                return;
            }

            const replyContent =
                res.data?.aiResponse ||
                res.data?.content ||
                res.data?.data?.aiResponse ||
                "";
            const returnedChatId =
                res.data?.chatId || res.data?.data?.chatId || startingChatId;

            if (replyContent) {
                if (!startingChatId && returnedChatId) {
                    justCreatedChatRef.current = returnedChatId;
                    setActiveChatId(returnedChatId);
                    history.replace(
                        `/imported/binance/ai-news/${returnedChatId}`
                    );
                }
                fetchConversationsList();

                const aiMsgId = res.data?.messageId || nanoid();
                const aiMsg = {
                    id: aiMsgId,
                    role: "assistant",
                    content: replyContent,
                    createdAt: Date.now(),
                };

                setMessages((prev) => [...prev, aiMsg]);
            }
        } catch (error) {
            if (error.response?.status === 401) {
                setRequiresAuth(true);
            } else if (currentSeq === requestSeqRef.current) {
                notification.error({
                    message: "Error",
                    description:
                        error.response?.data?.message ||
                        "Failed to get AI response. Please try again.",
                });
            }
        } finally {
            if (currentSeq === requestSeqRef.current) {
                setLoading(false);
            }
        }
    };

    const onFinishFailed = () => {};

    const goBackCTA = () => {
        history.push("/imported/binance");
    };

    const renderHistoryContent = () => {
        if (historyLoading) {
            return (
                <div style={{ padding: "12px 16px" }}>
                    <Skeleton active paragraph={{ rows: 4 }} />
                </div>
            );
        }

        const normalizedSearch = chatSearch.trim().toLowerCase();
        const filteredConversations = normalizedSearch
            ? conversations.filter((c) =>
                  cleanChatTitle(c.title)
                      .toLowerCase()
                      .includes(normalizedSearch)
              )
            : conversations;

        if (filteredConversations.length === 0) {
            return (
                <div
                    style={{
                        padding: "24px 16px",
                        textAlign: "center",
                        color: Tokens.textMuted,
                        fontSize: "13px",
                    }}
                >
                    {normalizedSearch
                        ? "No matching chats"
                        : "No previous chats"}
                </div>
            );
        }

        return (
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    width: "100%",
                }}
            >
                {filteredConversations.map((item) => {
                    const isActive = item.id === activeChatId;
                    return (
                        <div
                            key={item.id}
                            data-testid="chat-history-item"
                            role="button"
                            tabIndex={0}
                            onClick={() => handleSelectConversation(item.id)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleSelectConversation(item.id);
                                }
                            }}
                            style={{
                                padding: "8px 10px",
                                borderRadius: Tokens.radiusControl,
                                cursor: "pointer",
                                backgroundColor: isActive
                                    ? Tokens.raised
                                    : "transparent",
                                border: isActive
                                    ? `1px solid ${Tokens.hairline}`
                                    : "1px solid transparent",
                                display: "flex",
                                flexDirection: "row",
                                flexWrap: "nowrap",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: "8px",
                                width: "100%",
                                minWidth: 0,
                                boxSizing: "border-box",
                                transition:
                                    "background-color 150ms cubic-bezier(0.22, 1, 0.36, 1)",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    flex: 1,
                                    minWidth: 0,
                                    overflow: "hidden",
                                }}
                            >
                                <MessageOutlined
                                    style={{
                                        color: isActive
                                            ? Tokens.textPrimary
                                            : Tokens.textMuted,
                                        fontSize: "14px",
                                        flexShrink: 0,
                                    }}
                                />
                                <span
                                    style={{
                                        color: isActive
                                            ? Tokens.textPrimary
                                            : Tokens.textSecondary,
                                        fontSize: "13px",
                                        fontWeight: isActive ? 500 : 400,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        flex: 1,
                                        minWidth: 0,
                                    }}
                                >
                                    {cleanChatTitle(item.title)}
                                </span>
                            </div>
                            {confirmDeleteId === item.id ? (
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                        flexShrink: 0,
                                    }}
                                >
                                    <Button
                                        size="small"
                                        danger
                                        type="primary"
                                        data-testid="confirm-delete-chat-btn"
                                        onClick={(e) =>
                                            confirmDeleteConversation(
                                                e,
                                                item.id
                                            )
                                        }
                                        style={{
                                            height: "24px",
                                            padding: "0 6px",
                                            fontSize: "11px",
                                            borderRadius: Tokens.radiusTag,
                                        }}
                                    >
                                        Delete
                                    </Button>
                                    <Button
                                        size="small"
                                        data-testid="cancel-delete-chat-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setConfirmDeleteId(null);
                                        }}
                                        style={{
                                            height: "24px",
                                            padding: "0 6px",
                                            fontSize: "11px",
                                            borderRadius: Tokens.radiusTag,
                                            backgroundColor: Tokens.raised,
                                            borderColor: Tokens.hairline,
                                            color: Tokens.textSecondary,
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            ) : (
                                <Button
                                    type="text"
                                    data-testid="delete-chat-btn"
                                    icon={
                                        <DeleteOutlined
                                            style={{
                                                color: Tokens.textMuted,
                                                fontSize: "12px",
                                            }}
                                        />
                                    }
                                    onClick={(e) =>
                                        handleDeleteConversation(e, item.id)
                                    }
                                    title="Delete chat"
                                    style={{
                                        width: "28px",
                                        height: "28px",
                                        minWidth: "28px",
                                        flexShrink: 0,
                                        padding: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderMessagesContent = () => {
        if (messagesLoading) {
            return (
                <div
                    style={{
                        padding: "40px",
                        textAlign: "center",
                        display: "flex",
                        justifyContent: "center",
                    }}
                >
                    <Spin size="large" />
                </div>
            );
        }

        if (requiresAuth) {
            return (
                <LoginWall
                    title="Sign in to use Ask AI Assistant"
                    description="Sign in to chat with your AI portfolio strategist using live 1W/1D market technicals, 14-source crypto news, and your reconciled cost basis."
                    features={[
                        "Personalized analysis grounded in your portfolio & break-even prices",
                        "Live 1W/1D Binance candles, RSI, and support/resistance levels",
                        "14-source crypto & macro RSS intelligence with persistent chat history",
                    ]}
                    returnPath="/imported/binance/ai-news"
                />
            );
        }

        if (messages.length === 0 && !loading) {
            let assetChips = ["BTC", "XRP", "SOL", "ZEC", "XLM", "INJ", "ONDO"];
            const manualSymbols = Array.isArray(manualAssets)
                ? manualAssets
                      .map((a) =>
                          String(a?.symbol || a?.id || "")
                              .toUpperCase()
                              .slice(0, 8)
                      )
                      .filter((s) => s && s !== "USDT" && s !== "USDC")
                : [];
            try {
                const cachedSummary = JSON.parse(
                    localStorage.getItem("binance_wallet_summary_cache") || "{}"
                );
                const cachedAssets = Array.isArray(cachedSummary?.assets)
                    ? cachedSummary.assets
                          .map((a) => String(a?.name || "").toUpperCase())
                          .filter((s) => s && s !== "USDT" && s !== "USDC")
                    : [];
                if (cachedAssets.length > 0 || manualSymbols.length > 0) {
                    assetChips = Array.from(
                        new Set(["BTC", ...cachedAssets, ...manualSymbols])
                    ).slice(0, 8);
                }
            } catch (e) {
                if (manualSymbols.length > 0) {
                    assetChips = Array.from(
                        new Set(["BTC", ...manualSymbols, ...assetChips])
                    ).slice(0, 8);
                }
            }

            const playbookPrompts = [
                {
                    title: "Weekly close & market outlook",
                    subtitle: "1W/1D candle structure, RSI & support levels",
                    prompt: "Does it appear that the market will go down further after the latest weekly close? Check BTC and my top holdings.",
                },
                {
                    title: "Break-even & portfolio risk audit",
                    subtitle: "Compare live prices vs my average buy prices",
                    prompt: "Audit my active holdings against my average buy prices — where am I most exposed and which positions are closest to break-even?",
                },
                {
                    title: "Deploy liquid USDT dry powder",
                    subtitle: "DCA levels & cash allocation strategy",
                    prompt: "Based on current weekly/daily support levels and my portfolio weights, how should I deploy or hold my liquid USDT?",
                },
                {
                    title: "Take-profit & trim targets",
                    subtitle: "Resistance zones for outperforming coins",
                    prompt: "Which of my positions are outperforming or near resistance, and what price targets make sense for taking partial profit?",
                },
            ];

            return (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: 1,
                        padding: isMobile ? "16px 8px" : "32px 24px",
                        textAlign: "center",
                    }}
                >
                    <div
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            flexWrap: "wrap",
                            justifyContent: "center",
                            marginBottom: "12px",
                        }}
                    >
                        {[
                            "14 RSS Sources + Live Search",
                            "1W/1D Candles, RVOL & RSI",
                            "Cost Basis & Break-Evens",
                        ].map((badge) => (
                            <span
                                key={badge}
                                style={{
                                    fontSize: "11px",
                                    fontFamily: Tokens.fontMono,
                                    color: Tokens.accent,
                                    backgroundColor: Tokens.accentSubtle,
                                    border: `1px solid ${Tokens.accentBorder}`,
                                    borderRadius: Tokens.radiusTag,
                                    padding: "2px 8px",
                                }}
                            >
                                {badge}
                            </span>
                        ))}
                    </div>
                    <h2
                        style={{
                            color: Tokens.textPrimary,
                            fontWeight: 500,
                            marginBottom: "6px",
                            fontSize: isMobile ? "19px" : "24px",
                        }}
                    >
                        Ask about your portfolio or market structure
                    </h2>
                    <p
                        style={{
                            color: Tokens.textSecondary,
                            fontSize: "13px",
                            maxWidth: "480px",
                            lineHeight: "1.5",
                            margin: "0 0 12px",
                        }}
                    >
                        Grounded in live Binance weekly/daily candles, relative
                        volume, your exact average buy prices, and 14 crypto RSS
                        sources.
                    </p>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "6px",
                            flexWrap: "wrap",
                            maxWidth: "560px",
                            marginBottom: "14px",
                        }}
                    >
                        {assetChips.map((sym) => (
                            <button
                                key={sym}
                                type="button"
                                data-testid="ask-ai-asset-chip"
                                onClick={() => {
                                    setQuery(
                                        `Search latest ${sym} news and analyze its 1W/1D support, resistance, RSI, and my break-even position.`
                                    );
                                    setDisabled(false);
                                }}
                                style={{
                                    backgroundColor: Tokens.raised,
                                    border: `1px solid ${Tokens.hairline}`,
                                    borderRadius: Tokens.radiusTag,
                                    padding: "3px 10px",
                                    color: Tokens.textPrimary,
                                    fontSize: "11px",
                                    fontFamily: Tokens.fontMono,
                                    cursor: "pointer",
                                }}
                            >
                                ${sym}
                            </button>
                        ))}
                    </div>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: isMobile
                                ? "1fr"
                                : "repeat(2, minmax(0, 1fr))",
                            gap: "8px",
                            width: "100%",
                            maxWidth: "560px",
                            textAlign: "left",
                        }}
                    >
                        {playbookPrompts.map((item) => (
                            <button
                                key={item.title}
                                type="button"
                                data-testid="ask-ai-playbook-card"
                                onClick={() => {
                                    setQuery(item.prompt);
                                    setDisabled(false);
                                }}
                                style={{
                                    backgroundColor: Tokens.card,
                                    border: `1px solid ${Tokens.hairline}`,
                                    borderRadius: Tokens.radiusCard,
                                    padding: "10px 14px",
                                    cursor: "pointer",
                                    textAlign: "left",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "2px",
                                }}
                            >
                                <span
                                    style={{
                                        color: Tokens.textPrimary,
                                        fontSize: "13px",
                                        fontWeight: 500,
                                    }}
                                >
                                    {item.title}
                                </span>
                                <span
                                    style={{
                                        color: Tokens.textSecondary,
                                        fontSize: "12px",
                                    }}
                                >
                                    {item.subtitle}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            );
        }

        return (
            <React.Fragment>
                {messages.map((msg) => {
                    const isUser = msg.role === "user";

                    let bubbleMaxWidth = "85%";
                    if (isUser) {
                        bubbleMaxWidth = isMobile ? "88%" : "75%";
                    } else if (isMobile) {
                        bubbleMaxWidth = "95%";
                    }

                    const messageBody = isUser ? (
                        <span style={{ color: Tokens.textPrimary }}>
                            {msg.content}
                        </span>
                    ) : (
                        <MarkdownText content={msg.content} />
                    );

                    return (
                        <div
                            key={msg.id}
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: isUser ? "flex-end" : "flex-start",
                                marginBottom: "16px",
                                width: "100%",
                            }}
                        >
                            <div
                                style={{
                                    maxWidth: bubbleMaxWidth,
                                    backgroundColor: isUser
                                        ? Tokens.raised
                                        : Tokens.card,
                                    border: `1px solid ${Tokens.hairline}`,
                                    borderRadius: Tokens.radiusCard,
                                    padding: isMobile
                                        ? "12px 16px"
                                        : "16px 20px",
                                    color: Tokens.textPrimary,
                                    fontSize: "14px",
                                    wordBreak: "break-word",
                                }}
                            >
                                {messageBody}
                            </div>

                            <span
                                style={{
                                    fontSize: "11px",
                                    color: Tokens.textMuted,
                                    fontFamily: Tokens.fontMono,
                                    fontVariantNumeric: "tabular-nums",
                                    marginTop: "4px",
                                    padding: "0 4px",
                                }}
                            >
                                {msg.createdAt
                                    ? dayjs(msg.createdAt).format("hh:mm A")
                                    : ""}
                            </span>
                        </div>
                    );
                })}
                {loading && (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            marginBottom: "16px",
                            width: "100%",
                        }}
                    >
                        <div
                            style={{
                                backgroundColor: Tokens.card,
                                border: `1px solid ${Tokens.hairline}`,
                                borderRadius: Tokens.radiusCard,
                                padding: isMobile ? "12px 16px" : "14px 18px",
                                color: Tokens.textSecondary,
                                fontSize: "13px",
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                            }}
                        >
                            <Spin size="small" />
                            <span>
                                Analyzing portfolio and market headlines...
                            </span>
                        </div>
                    </div>
                )}
            </React.Fragment>
        );
    };

    const renderSidebarContents = () => (
        <div
            style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
            <Button
                type="default"
                icon={<PlusOutlined />}
                onClick={handleNewChat}
                style={{
                    backgroundColor: Tokens.raised,
                    borderColor: Tokens.hairline,
                    color: Tokens.textPrimary,
                    borderRadius: Tokens.radiusControl,
                    fontWeight: 500,
                    marginBottom: "10px",
                    height: "36px",
                }}
            >
                New chat
            </Button>
            <Input
                placeholder="Search chats..."
                data-testid="chat-history-search"
                allowClear
                value={chatSearch}
                onChange={(e) => setChatSearch(e.target.value)}
                style={{
                    backgroundColor: Tokens.card,
                    borderColor: Tokens.hairline,
                    color: Tokens.textPrimary,
                    borderRadius: Tokens.radiusControl,
                    marginBottom: "12px",
                    height: "34px",
                    fontSize: "13px",
                }}
            />
            <div style={{ flex: 1, overflowY: "auto" }}>
                {renderHistoryContent()}
            </div>
        </div>
    );

    const renderMainHeader = () => (
        <header
            data-testid="app-shell-header"
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: isMobile ? "12px 16px" : "16px 24px",
                backgroundColor: Tokens.bg,
                borderBottom: `1px solid ${Tokens.hairline}`,
                height: isMobile ? "56px" : "64px",
                minHeight: isMobile ? "56px" : "64px",
                width: "100%",
                minWidth: 0,
                boxSizing: "border-box",
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    minWidth: 0,
                    flex: 1,
                }}
            >
                <Button
                    type="text"
                    icon={
                        <ArrowLeftOutlined
                            style={{
                                color: Tokens.textSecondary,
                                fontSize: "16px",
                            }}
                        />
                    }
                    onClick={goBackCTA}
                    title="Back to portfolio"
                    style={{
                        padding: 0,
                        width: "32px",
                        height: "32px",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                />
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        minWidth: 0,
                    }}
                >
                    <span
                        style={{
                            fontSize: isMobile ? "15px" : "16px",
                            fontWeight: 500,
                            color: Tokens.textPrimary,
                            lineHeight: "1.2",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        Ask AI assistant
                    </span>
                    {!isMobile && (
                        <span
                            style={{
                                fontSize: "12px",
                                color: Tokens.textSecondary,
                                marginTop: "2px",
                            }}
                        >
                            Portfolio and crypto news assistant
                        </span>
                    )}
                </div>
                <AutoSyncBadge />
            </div>

            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flexShrink: 0,
                }}
            >
                {isMobile && (
                    <React.Fragment>
                        <Button
                            type="text"
                            icon={
                                <PlusOutlined
                                    style={{
                                        fontSize: "15px",
                                        color: Tokens.textSecondary,
                                    }}
                                />
                            }
                            onClick={handleNewChat}
                            title="New chat"
                            style={{
                                padding: 0,
                                width: "36px",
                                height: "36px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: Tokens.raised,
                                border: `1px solid ${Tokens.hairline}`,
                                borderRadius: Tokens.radiusControl,
                            }}
                        />
                        <Button
                            type="text"
                            icon={
                                <MenuOutlined
                                    style={{
                                        fontSize: "16px",
                                        color: Tokens.textSecondary,
                                    }}
                                />
                            }
                            onClick={() => setMobileMenuOpen(true)}
                            title="Chat history"
                            style={{
                                padding: 0,
                                width: "36px",
                                height: "36px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: Tokens.raised,
                                border: `1px solid ${Tokens.hairline}`,
                                borderRadius: Tokens.radiusControl,
                            }}
                        />
                    </React.Fragment>
                )}
            </div>
        </header>
    );

    return (
        <div
            data-testid="app-shell"
            style={{
                position: "fixed",
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                width: "100%",
                maxWidth: "100vw",
                height: "100%",
                backgroundColor: Tokens.bg,
                display: "flex",
                flexDirection: "row",
                overflow: "hidden",
            }}
        >
            {!isMobile && (
                <div
                    style={{
                        width: "260px",
                        minWidth: "260px",
                        height: "100%",
                        backgroundColor: Tokens.deepest,
                        borderRight: `1px solid ${Tokens.hairline}`,
                        padding: "16px",
                        display: "flex",
                        flexDirection: "column",
                        boxSizing: "border-box",
                    }}
                >
                    {renderSidebarContents()}
                </div>
            )}

            <div
                style={{
                    flex: 1,
                    minWidth: 0,
                    width: "100%",
                    maxWidth: "100%",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    backgroundColor: Tokens.bg,
                }}
            >
                {renderMainHeader()}

                <main
                    data-testid="app-shell-main"
                    style={{
                        flex: 1,
                        minWidth: 0,
                        width: "100%",
                        boxSizing: "border-box",
                        overflowY: "auto",
                        overflowX: "hidden",
                        padding: isMobile ? "16px 12px" : "24px",
                        backgroundColor: Tokens.bg,
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            minHeight: "100%",
                            maxWidth: "760px",
                            margin: "0 auto",
                            width: "100%",
                            minWidth: 0,
                            boxSizing: "border-box",
                        }}
                    >
                        {renderMessagesContent()}
                        <div ref={messagesEndRef} />
                    </div>
                </main>

                {!requiresAuth && (
                    <div
                        style={{
                            backgroundColor: Tokens.bg,
                            padding: isMobile
                                ? "10px 12px 16px"
                                : "16px 24px 28px",
                            borderTop: `1px solid ${Tokens.hairline}`,
                            display: "flex",
                            justifyContent: "center",
                            width: "100%",
                            minWidth: 0,
                            boxSizing: "border-box",
                        }}
                    >
                        <div
                            style={{
                                width: "100%",
                                maxWidth: "760px",
                                minWidth: 0,
                                boxSizing: "border-box",
                            }}
                        >
                            {messages.length === 0 && (
                                <div
                                    style={{
                                        display: "flex",
                                        gap: "8px",
                                        marginBottom: "8px",
                                        overflowX: "auto",
                                        WebkitOverflowScrolling: "touch",
                                        maxWidth: "100%",
                                        minWidth: 0,
                                        paddingBottom: "4px",
                                    }}
                                >
                                    {[
                                        "Market sentiment analysis",
                                        "Analyze USDT yields",
                                        "Top portfolio gainers",
                                        "Simple Earn APY advice",
                                    ].map((promptText) => (
                                        <Tag
                                            key={promptText}
                                            onClick={() => {
                                                setQuery(promptText);
                                                setDisabled(false);
                                            }}
                                            style={{
                                                backgroundColor: Tokens.card,
                                                color: Tokens.textSecondary,
                                                border: `1px solid ${Tokens.hairline}`,
                                                borderRadius: Tokens.radiusTag,
                                                padding: "4px 12px",
                                                cursor: "pointer",
                                                fontSize: "12px",
                                                fontWeight: 400,
                                                userSelect: "none",
                                                whiteSpace: "nowrap",
                                                flexShrink: 0,
                                                margin: 0,
                                            }}
                                        >
                                            {promptText}
                                        </Tag>
                                    ))}
                                </div>
                            )}

                            <Form
                                form={form}
                                onFinish={onFinish}
                                onFinishFailed={onFinishFailed}
                                name="ask-ai"
                                autoComplete="off"
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "flex-end",
                                        backgroundColor: Tokens.card,
                                        border: `1px solid ${Tokens.hairline}`,
                                        borderRadius: Tokens.radiusCard,
                                        padding: "8px 12px",
                                        gap: "8px",
                                        width: "100%",
                                        minWidth: 0,
                                        boxSizing: "border-box",
                                    }}
                                >
                                    <Form.Item
                                        style={{
                                            marginBottom: 0,
                                            flex: 1,
                                            minWidth: 0,
                                        }}
                                    >
                                        <Input.TextArea
                                            placeholder={
                                                isMobile
                                                    ? "Ask AI"
                                                    : "Ask about your holdings or market news"
                                            }
                                            bordered={false}
                                            value={query}
                                            onChange={handleValueChange}
                                            onKeyDown={handleKeyDown}
                                            maxLength={500}
                                            autoSize={{
                                                minRows: 1,
                                                maxRows: 6,
                                            }}
                                            style={{
                                                backgroundColor: "transparent",
                                                color: Tokens.textPrimary,
                                                border: "none",
                                                boxShadow: "none",
                                                padding: "4px 0",
                                                fontSize: "14px",
                                                resize: "none",
                                            }}
                                        />
                                    </Form.Item>
                                    <Button
                                        disabled={disabled}
                                        type="text"
                                        htmlType="submit"
                                        loading={loading}
                                        icon={
                                            <SendOutlined
                                                style={{
                                                    fontSize: "16px",
                                                    color: disabled
                                                        ? Tokens.textMuted
                                                        : Tokens.textPrimary,
                                                }}
                                            />
                                        }
                                        style={{
                                            border: "none",
                                            background: "transparent",
                                            width: "32px",
                                            height: "32px",
                                            flexShrink: 0,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            padding: 0,
                                        }}
                                    />
                                </div>
                            </Form>
                        </div>
                    </div>
                )}
                <BottomNavBar activeTab="ask-ai" />
            </div>

            {isMobile && (
                <Drawer
                    title={
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                            }}
                        >
                            <span
                                style={{
                                    color: Tokens.textPrimary,
                                    fontSize: "15px",
                                    fontWeight: 500,
                                }}
                            >
                                Chat history
                            </span>
                            <Button
                                type="text"
                                icon={
                                    <CloseOutlined
                                        style={{
                                            color: Tokens.textSecondary,
                                            fontSize: "14px",
                                        }}
                                    />
                                }
                                onClick={() => setMobileMenuOpen(false)}
                                title="Close chat history"
                                style={{
                                    width: "32px",
                                    height: "32px",
                                    padding: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            />
                        </div>
                    }
                    placement="left"
                    closable={false}
                    push={false}
                    onClose={() => setMobileMenuOpen(false)}
                    open={mobileMenuOpen}
                    width={Math.min(windowWidth - 48, 300)}
                    maskStyle={{
                        backgroundColor: "rgba(0, 0, 0, 0.75)",
                    }}
                    bodyStyle={{
                        backgroundColor: Tokens.deepest,
                        padding: "16px",
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        boxSizing: "border-box",
                    }}
                    headerStyle={{
                        backgroundColor: Tokens.deepest,
                        borderBottom: `1px solid ${Tokens.hairline}`,
                        padding: "12px 16px",
                    }}
                >
                    {renderSidebarContents()}
                </Drawer>
            )}
        </div>
    );
};

export default News;
